import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import { Grid, Autocomplete, Paper, IconButton, Tooltip, Snackbar } from "@mui/material";
import { Card } from "react-bootstrap";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert, AlertTitle, CircularProgress } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import Draggable from 'react-draggable';
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import ManualAyuda from "../../controls/ManualAyuda";
import MDButton from "../../controls/MDButton";
import MDInput from "../../controls/MDInput";
import DataTable from "../../controls/Tables/DataTable";
import dayjs from "dayjs";
import { postData, deleteData } from "../../Utils/Http/api";
import { fetchOrganismos } from "../../Utils/Organismo";
import { fetchMotivosCualitativos } from "../../Utils/MotivoCualitativo";

// Componente para hacer los diálogos arrastrables
function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const ImportarArchivo = () => {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [tipoArchivo, setTipoArchivo] = useState(2);
    const [mensaje, setMensaje] = useState("");
    const [exito, setExito] = useState(false);
    const [grabando, setGrabando] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [organismos, setOrganismos] = useState([]);
    const [selectedOrganismo, setSelectedOrganismo] = useState(null);
    const [selectedPeriodo, setSelectedPeriodo] = useState(null);
    const [motivos, setMotivos] = useState([]);
    const [selectedMotivo, setSelectedMotivo] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const tiposArchivo = [
        { id: 2, descripcion: "PRE-JUDICIAL" },
        { id: 3, descripcion: "JUDICIAL" },
        { id: 4, descripcion: "PRE-JUDICIAL ACTUALIZAR OBJETOS Y MONTOS" },
        { id: 5, descripcion: "CUALITATIVO" },
        { id: 6, descripcion: "CUALITATIVO CIERRE (IDCaso;Monto)" },
    ];

    // Tipos de archivo de gestiones cualitativas: 5 importa casos (con motivo de campaña) y 6 los cierra.
    const TIPO_CUALITATIVO = 5;
    const TIPO_CIERRE_CUALITATIVO = 6;

    const defaultHasta = dayjs().format("YYYY-MM-DD");
    const defaultDesde = dayjs().subtract(30, "day").format("YYYY-MM-DD");
    const [filters, setFilters] = useState({
        nombreArchivo: "",
        fechaSubidadesde: defaultDesde,
        fechaSubidahasta: defaultHasta,
        usuario: "",
    });

    const periodOptions = useMemo(() => {
        const meses = [
            "ENE",
            "FEB",
            "MAR",
            "ABR",
            "MAY",
            "JUN",
            "JUL",
            "AGO",
            "SEP",
            "OCT",
            "NOV",
            "DIC",
        ];
        const base = dayjs();
        const options = [];
        for (let i = -3; i <= 3; i += 1) {
            const date = base.add(i, "month");
            const year = date.year();
            const monthNumber = String(date.month() + 1).padStart(2, "0");
            const label = `${year}${monthNumber}-${meses[date.month()]}`;
            options.push({ value: label, label });
        }
        return options;
    }, []);

    const usuarioSubida = localStorage.getItem("userlogueado") || "";

    const handleDeleteClick = (row) => {
        setDeleteTarget(row);
        setOpenDeleteDialog(true);
    };

    const columns = useMemo(() => ([
        { Header: "ID", accessor: "idArchivoSubido", width: "8%" },
        { Header: "Nombre", accessor: "nombreArchivo", width: "25%" },
        { Header: "Hash", accessor: "hashArchivo", width: "30%", Cell: ({ value }) => <span title={value}>{(value || "").slice(0, 24)}{value && value.length > 24 ? "…" : ""}</span> },
        { Header: "Fecha", accessor: "fechaSubida", width: "12%", Cell: ({ value }) => dayjs(value).format("DD/MM/YYYY HH:mm") },
        { 
            Header: "Periodo",
            accessor: (row) => row.periodoGestion ?? row.PeriodoGestion ?? "",
            id: "periodoGestion",
            width: "10%",
        },
        { Header: "Usuario", accessor: "usuarioSubida", width: "8%" },
        { Header: "Estado", accessor: "estado", width: "8%" },
        { Header: "Tipo", accessor: "tipoArchivo", width: "8%", Cell: ({ value }) => tiposArchivo.find(t => t.id === value)?.descripcion || value },
        { 
            Header: "Acciones", 
            accessor: "acciones", 
            width: "5%",
            Cell: ({ row }) => {
                const eliminable = row.original.eliminable;
                const getTooltipText = () => {
                    if (eliminable === 1) return "Eliminar archivo y casos relacionados";
                    if (eliminable === 2) return "Archivo ya eliminado";
                    return "No se puede eliminar";
                };
                const getIconColor = () => {
                    if (eliminable === 1) return "error";
                    if (eliminable === 2) return "warning";
                    return "disabled";
                };
                return (
                    <MDBox display="flex" justifyContent="center">
                        <Tooltip title={getTooltipText()} arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => eliminable === 1 && handleDeleteClick(row.original)}
                                    disabled={eliminable !== 1}
                                    aria-label="eliminar"
                                >
                                    <DeleteIcon color={getIconColor()} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </MDBox>
                );
            }
        },
    ]), []);

    const table = useMemo(() => ({
        columns,
        rows,
    }), [columns, rows]);

    const fetchData = useCallback(async (useFilters = true) => {
        setLoading(true);
        try {
            const req = useFilters
                ? {
                    nombre: filters.nombreArchivo || "",
                    fechaSubidadesde: filters.fechaSubidadesde || "",
                    fechaSubidahasta: filters.fechaSubidahasta || "",
                    usuarioSubida: filters.usuario || "",
                }
                : {
                    nombre: "",
                    fechaSubidadesde: "",
                    fechaSubidahasta: "",
                    usuario: "",
                };

            const res = await postData("/ArchivoSubidoListar", req);
            const list = res?.value || [];
            setRows(list);
            setRefreshKey((k) => k + 1);
        } catch (ex) {
            console.error("Error cargando archivos:", ex);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData(true);
    }, [fetchData]);

    useEffect(() => {
        const loadOrganismos = async () => {
            try {
                console.log("Cargando organismos...");
                const data = await fetchOrganismos();
                console.log("Organismos recibidos:", data);
                setOrganismos(data);
            } catch (error) {
                console.error("Error al cargar organismos:", error);
                setOrganismos([]);
            }
        };
        loadOrganismos();
    }, []);

    const handleOpen = () => {
        setFile(null);
        setTipoArchivo(2);
        setMensaje("");
        setExito(false);
        setSelectedOrganismo(null);
        setSelectedPeriodo(periodOptions[3] || null);
        setSelectedMotivo(null);
        setOpen(true);
        // Solo se ofrecen los motivos activos.
        fetchMotivosCualitativos(true).then(setMotivos);
    };

    const handleClose = () => {
        if (!grabando) setOpen(false);
    };

    const handleUpload = async () => {
        try {
            if (!file) {
                setMensaje("Seleccioná un archivo");
                setExito(false);
                return;
            }
            // El cierre cualitativo se arma solo con IDCaso y Monto: no lleva organismo.
            if (!selectedOrganismo && tipoArchivo !== TIPO_CIERRE_CUALITATIVO) {
                setMensaje("Seleccioná un organismo");
                setExito(false);
                return;
            }
            if (tipoArchivo === TIPO_CUALITATIVO && !selectedMotivo) {
                setMensaje("Seleccioná el motivo de la campaña cualitativa");
                setExito(false);
                return;
            }
            if (!selectedPeriodo) {
                setMensaje("Seleccioná un periodo");
                setExito(false);
                return;
            }
            setGrabando(true);
            setMensaje("");
            const formData = new FormData();
            formData.append("Archivo", file);
            formData.append("UsuarioSubida", usuarioSubida);
            formData.append("TipoArchivo", tipoArchivo);
            formData.append("IdOrganismo", selectedOrganismo?.idOrganismo ?? 0);
            formData.append("PeriodoGestion", selectedPeriodo.value);
            if (tipoArchivo === TIPO_CUALITATIVO) {
                formData.append("IDMotivoCualitativo", selectedMotivo.idMotivoCualitativo);
            }

            const res = await postData("/AddArchivoSubido", formData);
            if (res?.isSuccess && res?.value) {
                await fetchData(true);
                setExito(true);
                setMensaje("Archivo subido correctamente");
                setOpen(false);
                // Resumen de la importacion: cuantos registros se procesaron y los errores que haya habido.
                const r = res.value;
                const errores = r.errores || [];
                setSnackbar({
                    open: true,
                    message: `Archivo subido. Insertados: ${r.insertados ?? 0} - Actualizados: ${r.actualizados ?? 0} - Rechazados: ${r.rechazados ?? 0}` +
                        (errores.length > 0 ? `. Errores: ${errores.slice(0, 3).join(" | ")}${errores.length > 3 ? ` (+${errores.length - 3} más)` : ""}` : ""),
                    severity: errores.length > 0 || (r.rechazados ?? 0) > 0 ? 'warning' : 'success'
                });
            } else {
                setExito(false);
                setMensaje(res?.error || "Error al subir el archivo");
            }
        } catch (error) {
            // postData lanza Error con message del backend (detail/title)
            setExito(false);
            setMensaje(error?.message || "Error al subir el archivo");
        } finally {
            setGrabando(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const endpoint = `/ArchivoSubidoDelete/${deleteTarget.idArchivoSubido},${deleteTarget.tipoArchivo}`;
            const res = await deleteData(endpoint);
            if (res?.isSuccess) {
                setSnackbar({ open: true, message: 'Archivo y casos relacionados eliminados correctamente', severity: 'success' });
                await fetchData(true);
            } else {
                setSnackbar({ open: true, message: res?.error || 'Error al eliminar el archivo', severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: error?.message || 'Error al eliminar el archivo', severity: 'error' });
        } finally {
            setOpenDeleteDialog(false);
            setDeleteTarget(null);
        }
    };

    const handleCancelDelete = () => {
        setOpenDeleteDialog(false);
        setDeleteTarget(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    <Grid container spacing={6}>
                        <Grid item xs={12}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={3}
                                    px={2}
                                    variant="gradient"
                                    bgColor="secondary"
                                    borderRadius="lg"
                                    coloredShadow="light"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDTypography variant="h6" color="white">
                                        Importar archivos
                                    </MDTypography>
                                    <ManualAyuda seccion="Importar Sujetos y Casos" />
                                </MDBox>

                                <MDBox pt={3} py={3} px={2}>
                                    <MDBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                        <MDInput
                                            name="nombreArchivo"
                                            label="Nombre archivo"
                                            value={filters.nombreArchivo}
                                            onChange={(e) => setFilters({ ...filters, nombreArchivo: e.target.value })}
                                        />
                                        <TextField
                                            type="date"
                                            label="Fecha desde"
                                            InputLabelProps={{ shrink: true }}
                                            value={filters.fechaSubidadesde}
                                            onChange={(e) => setFilters({ ...filters, fechaSubidadesde: e.target.value })}
                                        />
                                        <TextField
                                            type="date"
                                            label="Fecha hasta"
                                            InputLabelProps={{ shrink: true }}
                                            value={filters.fechaSubidahasta}
                                            onChange={(e) => setFilters({ ...filters, fechaSubidahasta: e.target.value })}
                                        />
                                        <MDInput
                                            name="usuario"
                                            label="Usuario"
                                            value={filters.usuario}
                                            onChange={(e) => setFilters({ ...filters, usuario: e.target.value })}
                                        />
                                        <MDButton variant="contained" color="info" onClick={() => fetchData(true)}>Buscar</MDButton>
                                        <MDButton variant="text" color="dark" onClick={() => { setFilters({ nombreArchivo: "", fechaSubidadesde: defaultDesde, fechaSubidahasta: defaultHasta, usuario: "" }); fetchData(true); }}>Limpiar</MDButton>
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <MDButton variant="gradient" color="info" onClick={handleOpen}>Subir archivo</MDButton>
                                    </MDBox>

                                    {loading ? (
                                        <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                                            <CircularProgress />
                                        </MDBox>
                                    ) : (
                                        <DataTable
                                            key={refreshKey}
                                            table={table}
                                            entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50, 100] }}
                                            canSearch={false}
                                            showTotalEntries={true}
                                            isSorted={false}
                                            noEndBorder={false}
                                            pagination={{ variant: "gradient", color: "info" }}
                                            scrollHeight="60vh"
                                        />
                                    )}
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>
                </MDBox>
            </DashboardLayout>
            <Dialog open={open} onClose={(event, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose(); }} fullWidth maxWidth="sm" disableEscapeKeyDown PaperComponent={PaperComponent}>
                <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>Subir archivo</DialogTitle>
                <DialogContent>
                    <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
                        <MDBox display="flex" gap={2} alignItems="center">
                            <TextField
                                label="Archivo seleccionado"
                                value={file?.name || ""}
                                placeholder="Ninguno"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                            <MDButton component="label" variant="contained" color="info">
                                Seleccionar archivo
                                <input hidden type="file" onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                            </MDButton>
                            {file && (
                                <MDButton variant="text" color="error" onClick={() => setFile(null)}>
                                    Quitar
                                </MDButton>
                            )}
                        </MDBox>
                        
                        <Autocomplete
                            options={organismos}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={selectedOrganismo}
                            onChange={(event, newValue) => setSelectedOrganismo(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Organismo" placeholder="Seleccionar organismo" />
                            )}
                            fullWidth
                        />
                        
                        <Autocomplete
                            options={tiposArchivo}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={tiposArchivo.find(t => t.id === tipoArchivo) || tiposArchivo[0]}
                            onChange={(event, newValue) => setTipoArchivo(newValue?.id || 2)}
                            renderInput={(params) => (
                                <TextField {...params} label="Tipo de Archivo" placeholder="Seleccionar tipo" />
                            )}
                            fullWidth
                        />

                        {tipoArchivo === TIPO_CUALITATIVO && (
                            <Autocomplete
                                options={motivos}
                                getOptionLabel={(option) => option.descripcion || ""}
                                isOptionEqualToValue={(option, value) => option.idMotivoCualitativo === value.idMotivoCualitativo}
                                value={selectedMotivo}
                                onChange={(event, newValue) => setSelectedMotivo(newValue)}
                                noOptionsText="No hay motivos activos (Configuraciones → Motivos Campaña Cualitativa)"
                                renderInput={(params) => (
                                    <TextField {...params} label="Motivo de la campaña cualitativa" placeholder="Seleccionar motivo" required />
                                )}
                                fullWidth
                            />
                        )}

                        <Autocomplete
                            options={periodOptions}
                            getOptionLabel={(option) => option.label || ""}
                            value={selectedPeriodo}
                            onChange={(event, newValue) => setSelectedPeriodo(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Periodo de gestion" placeholder="Seleccionar periodo" required />
                            )}
                            fullWidth
                        />
                        
                        <TextField
                            label="Usuario"
                            value={usuarioSubida}
                            fullWidth
                            disabled
                        />
                        {mensaje && (
                            <MDBox mb={2}>
                                <Alert severity={exito ? "success" : "error"}>
                                    <AlertTitle>{exito ? "Éxito" : "Error"}</AlertTitle>
                                    {mensaje}
                                </Alert>
                            </MDBox>
                        )}
                    </MDBox>

                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleClose} color="error" variant="gradient" disabled={grabando}>Cancelar</MDButton>
                    <MDButton onClick={handleUpload} color="success" variant="gradient" disabled={grabando}>
                        {grabando ? (
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} color="inherit" />
                                <span>Procesando...</span>
                            </MDBox>
                        ) : (
                            "Aceptar"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openDeleteDialog} 
                onClose={(event, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCancelDelete(); }} 
                fullWidth 
                maxWidth="sm" 
                disableEscapeKeyDown
                PaperComponent={PaperComponent}
            >
                <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
                    Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
                        <Alert severity="warning">
                            <AlertTitle>Advertencia</AlertTitle>
                            <MDTypography variant="body2">
                                Esta acción eliminará el archivo <strong>{deleteTarget?.nombreArchivo}</strong> y todos los casos relacionados a este archivo.
                            </MDTypography>
                            <MDTypography variant="body2" mt={1}>
                                ¿Estás seguro de que deseas continuar?
                            </MDTypography>
                        </Alert>
                        {deleteTarget && (
                            <MDBox>
                                <MDTypography variant="caption" color="text">
                                    <strong>ID:</strong> {deleteTarget.idArchivoSubido}
                                </MDTypography>
                                <br />
                                <MDTypography variant="caption" color="text">
                                    <strong>Tipo:</strong> {tiposArchivo.find(t => t.id === deleteTarget.tipoArchivo)?.descripcion || deleteTarget.tipoArchivo}
                                </MDTypography>
                            </MDBox>
                        )}
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCancelDelete} color="secondary" variant="gradient">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={handleConfirmDelete} color="error" variant="gradient">
                        Eliminar
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={snackbar.severity === 'warning' ? 15000 : 4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <MuiAlert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity} 
                    elevation={6} 
                    variant="filled"
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default ImportarArchivo;